import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useSearch } from '../../contexts/SearchContext';
import Button from '../buttons/Button';
import PillButton from '../buttons/Pill';
import CloseIcon from '../icons/Close';
import IconLink from './IconLink';

import styles from '../../../styles/partials/past-searches.module.scss';

const propTypes = {
    limit: PropTypes.number,
    onClickItem: PropTypes.func,
    isFocusable: PropTypes.bool,
    className: PropTypes.string,
    isDark: PropTypes.bool,
};

const defaultProps = {
    limit: 4,
    onClickItem: null,
    isFocusable: true,
    className: null,
    isDark: false,
};

const RecentSearches = ({ limit, isFocusable, className, onClickItem, isDark }) => {
    const intl = useIntl();
    const { recentSearches = null, removeRecentSearch, clearRecentSearches } = useSearch();

    const onClickRecentSearch = useCallback(
        (recentSearch) => {
            onClickItem(recentSearch);
        },
        [onClickItem],
    );

    const onClickClearAllRecentSearches = useCallback(
        (e) => {
            e.preventDefault();
            clearRecentSearches();
        },
        [clearRecentSearches],
    );

    const onClickRemoveRecentSearch = useCallback(
        (e, recentSearch) => {
            e.preventDefault();
            removeRecentSearch(recentSearch);
        },
        [removeRecentSearch],
    );

    const finalSearches =
        recentSearches !== null && recentSearches.length > 0
            ? recentSearches.filter(
                  (recentSearch) => !isEmpty(recentSearch) && isString(recentSearch),
              )
            : [];

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isFocusable]: isFocusable,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            {finalSearches !== null && finalSearches.length > 0 ? (
                <div className={styles.inner}>
                    <h2 className={styles.heading}>
                        <FormattedMessage
                            defaultMessage="Recent searches:"
                            description="Past searches heading"
                        />
                    </h2>
                    {finalSearches.map((recentSearch, i) =>
                        i < limit ? (
                            <div key={recentSearch} className={styles.entry}>
                                <IconLink
                                    onClick={() =>
                                        onClickRecentSearch(decodeURIComponent(recentSearch))
                                    }
                                    className={styles.entryBtn}
                                    isFocusable={isFocusable}
                                    isCompact
                                    isDark={isDark}
                                >
                                    {decodeURIComponent(recentSearch)}
                                </IconLink>
                                <PillButton
                                    className={classNames([styles.entryClearBtn])}
                                    icon={<CloseIcon />}
                                    onClick={(e) => onClickRemoveRecentSearch(e, recentSearch)}
                                    tabIndex={isFocusable ? '' : '-1'}
                                    title={intl.formatMessage({
                                        defaultMessage: 'Remove search entry',
                                        description: 'Button label',
                                    })}
                                    // isDark={isDark}
                                />
                            </div>
                        ) : null,
                    )}
                    <Button
                        onClick={onClickClearAllRecentSearches}
                        className={styles.clearAllLink}
                        isFocusable={isFocusable}
                    >
                        <FormattedMessage
                            defaultMessage="Clear all"
                            description="Past searches link"
                        />
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

RecentSearches.propTypes = propTypes;
RecentSearches.defaultProps = defaultProps;

export default RecentSearches;
